import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Check, Calendar, MapPin, Loader2, X } from "lucide-react";
import { ClientStatusSelect } from "@/shared/components/ClientStatusSelect";
import { ClientStatusType } from "@/shared/types/clientStatus";
import { IconButton } from "@/shared/components/ui/icon-button";

// Type for person/estate data from search query
interface PersonSearchResult {
  estate_id: number;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  date_of_death: string | null;
  location: string | null;
}

interface AssignEstateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lawyerUserId: number;
  onSuccess: () => void;
}

export function AssignEstateDialog({
  open,
  onOpenChange,
  lawyerUserId,
  onSuccess,
}: AssignEstateDialogProps) {
  const { user: currentUser } = useAppContext();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedPerson, setSelectedPerson] = useState<PersonSearchResult | null>(null);
  const [clientStatus, setClientStatus] = useState<ClientStatusType | null>(null);
  const [error, setError] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Search for persons (estates) with autosearch
  const { data: persons, isLoading: searchLoading } = useQuery({
    queryKey: ["search-persons-for-assignment", debouncedSearch, lawyerUserId],
    queryFn: async () => {
      if (!debouncedSearch.trim()) return [];

      const { data, error } = await supabase
        .from("estates")
        .select("estate_id, full_name, first_name, last_name, date_of_death, location")
        .eq("estate_type", "deceased")
        .or(
          `full_name.ilike.%${debouncedSearch}%,first_name.ilike.%${debouncedSearch}%,last_name.ilike.%${debouncedSearch}%`,
        )
        .limit(10);

      if (error) throw error;

      // Filter out persons already assigned to this lawyer
      const { data: existingAssignments } = await supabase
        .from("lawyer_estates")
        .select("estate_id")
        .eq("lawyer_user_id", lawyerUserId);

      const assignedPersonIds = new Set(existingAssignments?.map((a) => a.estate_id) || []);

      return (data || []).filter((person) => !assignedPersonIds.has(person.estate_id));
    },
    enabled: debouncedSearch.length >= 2,
  });

  // Mutation to assign estate
  const assignEstateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPerson) throw new Error("No estate selected");
      if (!clientStatus) throw new Error("No status selected");

      // Create the lawyer-person assignment
      const { error: assignError } = await supabase.from("lawyer_estates").insert({
        lawyer_user_id: lawyerUserId,
        estate_id: selectedPerson.estate_id,
        assigned_by: currentUser?.user_id,
        status: "active",
      });

      if (assignError) throw assignError;

      // Set the client status on the person
      const { error: statusError } = await supabase
        .from("estates")
        .update({ lawyer_client_status: clientStatus })
        .eq("estate_id", selectedPerson.estate_id);

      if (statusError) throw statusError;
    },
    onSuccess: () => {
      resetForm();
      onSuccess();
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to assign estate");
    },
  });

  const resetForm = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedPerson(null);
    setClientStatus(null);
    setError("");
  };

  const handleSelectPerson = (person: PersonSearchResult) => {
    setSelectedPerson(person);
  };

  const handleSubmit = () => {
    if (!selectedPerson) {
      setError("Please select an estate");
      return;
    }
    if (!clientStatus) {
      setError("Please select a status");
      return;
    }
    assignEstateMutation.mutate();
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const showResults = debouncedSearch.length >= 2;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg w-full max-w-[425px] focus:outline-none">
          <div className="p-6">
            {/* Close button */}
            <DialogPrimitive.Close asChild>
              <IconButton className="absolute right-4 top-4" label="Close" tooltip={false}>
                <X />
              </IconButton>
            </DialogPrimitive.Close>

            {/* Header */}
            <h2 className="text-xl font-medium text-stone-900">Assign Estate</h2>
            <p className="text-sm text-stone-500 mb-6">
              Search for an estate to assign to this lawyer
            </p>

            {/* Search Input */}
            <div className="space-y-2 mb-4">
              <label htmlFor="estate-search" className="text-sm text-stone-700">
                Search by name
              </label>
              <Input
                id="estate-search"
                placeholder="Start typing to search..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setSelectedPerson(null);
                }}
              />
            </div>

            {/* Loading */}
            {searchLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
              </div>
            )}

            {/* Search Results */}
            {showResults && !searchLoading && persons && persons.length > 0 && (
              <div className="border rounded-md divide-y max-h-[180px] overflow-y-auto mb-4">
                {persons.map((person) => (
                  <div
                    key={person.estate_id}
                    className={`px-3 py-2.5 flex items-center justify-between cursor-pointer transition-colors ${
                      selectedPerson?.estate_id === person.estate_id
                        ? "bg-stone-100"
                        : "hover:bg-stone-50"
                    }`}
                    onClick={() => handleSelectPerson(person)}
                  >
                    <div>
                      <p className="text-sm font-medium text-stone-900">
                        {person.full_name ||
                          `${person.first_name || ""} ${person.last_name || ""}`.trim() ||
                          "Unknown"}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {person.date_of_death && (
                          <span className="flex items-center gap-1 text-sm text-stone-500">
                            <Calendar className="h-3 w-3" />
                            {new Date(person.date_of_death).toLocaleDateString()}
                          </span>
                        )}
                        {person.location && (
                          <span className="flex items-center gap-1 text-sm text-stone-500">
                            <MapPin className="h-3 w-3" />
                            {person.location}
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedPerson?.estate_id === person.estate_id && (
                      <Check className="h-4 w-4 text-stone-600" />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* No results */}
            {showResults && !searchLoading && (!persons || persons.length === 0) && (
              <p className="text-sm text-stone-500 text-center py-3 mb-4">No estates found</p>
            )}

            {/* Status (shown when estate selected) */}
            {selectedPerson && (
              <div className="space-y-1.5 mb-4">
                <label className="text-sm text-stone-700">
                  Client Status <span className="text-red-500">*</span>
                </label>
                <ClientStatusSelect
                  value={clientStatus}
                  onValueChange={setClientStatus}
                  className="w-full"
                />
              </div>
            )}

            {/* Error */}
            {error && <FieldError message={error} className="mb-4" />}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!selectedPerson || !clientStatus || assignEstateMutation.isPending}
                className="bg-teal-600 hover:bg-teal-700 disabled:bg-stone-200 disabled:text-stone-400"
              >
                {assignEstateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Assign Estate
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
